/* eslint-disable prefer-promise-reject-errors */
import { Form, Input } from 'antd';
import { useEffect, useState } from 'react';
import { ContactEmail, FormSubmitButton } from '@/general';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  regEmail, regPhoneLogin, env, PLATFORM_NAME, dhgate, CHANNEL_MAP, PLATFORM_REG,
} from '@/constants';
import { loginVerifyEmail } from '@api';
import { handleErrors } from '@/utils/handleErrors';
import ErrorTips from '@/general/ErrorTips';
import { getReturnLogin } from '@api/login-page-claim';
import redInfo from '@/assets/login_page/red_info.svg';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { EmailVerification } from '@/components/common/email-verification';
import { generateStyle } from '@/utils';
import styles from './LoginForm.module.css';

const placeholderOrderNumberMap = {
  [PLATFORM_NAME.sidelineswap]: 'Swap ID',
  [PLATFORM_NAME.sezzle]: 'Contract ID',
};

export const ErrorMessage = ({ error }) => (
  <div className="flex gap-1 items-center text-sm my-1">
    <img alt="red info" src={redInfo} />
    {error}
  </div>
);

const LoginForm = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const location = useLocation();
  const { pathname } = location || {};
  const search = new URLSearchParams(location.search);
  const {
    email, order_number: orderNumber, signature, access_token: accessToken, forceDirect,
  } = Object.fromEntries(search.entries());
  const [loading, setLoading] = useState(false);
  const loginLayout = useSelector((state) => state?.layoutSetting?.loginLayout); // 获取 loginLayout 状态
  const returnConfig = loginLayout || {};
  const customStyleConfig = returnConfig?.customStyleConfig || {};
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState({
    show: false,
    type: undefined,
    msg: '',
  });

  const [loginAccessToken, setLoginAccessToken] = useState(null);
  const [loginEmailSend, setLoginEmailSend] = useState(false);
  const [loginAccessStatus, setLoginAccessStatus] = useState();

  const [showVerify, setShowVerify] = useState(false);

  // 因为登录页的placeholderOrderNumberMap 目前没有特别特殊的pathPlatform判断
  const pathPlatform = (pathname?.match(PLATFORM_REG) || [])[0];

  const orderLabelText = `${t('Order Number')} / ${placeholderOrderNumberMap[pathPlatform] || t('Cover ID')}`;
  const emailLabelText = t('Email Address / Phone Number');

  const handleFinish = async (values) => {
    setLoading(true);
    const { host, search } = window.location || {};
    let platform = (pathname?.match(PLATFORM_REG) || [])[0];// garmentory/treet/gently/poizon...
    if (pathname.indexOf(dhgate) > -1) {
      platform = dhgate;
    }
    const subDomain = host.endsWith('.myshopify.com') ? host?.replace('.myshopify.com', '') : '';
    try {
      const { code, message: msg, data } = await getReturnLogin(
        {
          channel: CHANNEL_MAP[platform] || platform,
          signature,
          platform,
          contactInfo: values.email,
          orderName: values.orderNumber,
          subDomain,
          merchantId: returnConfig?.merchantId,
          pathname: 'rc-login',
          accessToken,
          forceDirect: forceDirect === '1',
        },
      );

      if (code === 0) {
        // 解析现有的 search 参数（例如 "?email=xxx"）
        const params = new URLSearchParams(search);
        // 将 URLSearchParams 转换为普通对象
        const queryParams = Object.fromEntries(params.entries());
        // 添加 email 和 order_number 参数
        params.set('email', values.email);
        params.set('order_number', values.orderNumber);
        if (signature) {
          params.set('signature', signature);
        }
        // forceDirect 是为了方便mock进入4.0逻辑，模拟迁移后的登录
        const isRc4 = data.redirectToNewRc4 || queryParams?.forceDirect === '1';
        if (isRc4) {
          params.set('redirectToNewRc4', isRc4);
        } else {
          params.set('redirectToNewRc', data.redirectToNewRc4);
        }
        if (queryParams?.forceDirect) {
          params.set('forceDirect', queryParams?.forceDirect);
        }
        params.set('lng', i18next.language?.split('-')[0]);
        // 构建新的查询字符串
        const newSearch = `?${params.toString()}`;
        const envMapping = {
          prod: '',
          local: '-dev',
          dev1: '-dev',
        };
        const redirectEnv = envMapping[env] ?? `-${env}`;
        // console.log(redirectEnv, newSearch);

        // window.location.replace(`http://localhost:5174/customer-portal${loginLayout?.urlShopName ? `/${loginLayout?.urlShopName}` : ''}/auto-login${newSearch}`);
        window.location.replace(`https://resolution${redirectEnv}.seel.com/customer-portal${loginLayout?.urlShopName ? `/${loginLayout?.urlShopName}` : ''}/auto-login${newSearch}`);
      } else if (code === 201002) {
        // no policies
        setErrorMsg({
          show: true,
          type: 'error',
          msg: t('Sorry, the order cannot be found. Please check if the order number is correct and whether it has been canceled. If you have any other issues, please contact the merchant.'),
        });
        // } else if (code === 200020) {
        //   // cancelled
        //   setErrorMsg({
        //     show: true,
        //     type: "warning",
        //     msg: "Protection coverage has been canceled. You cannot report an issue for a package with canceled coverage.",
        //   });
      } else if ([200052, 200053].includes(code) && data?.accessToken) {
        const result = await loginVerifyEmail({
          accessToken: data.accessToken,
          rcVersion: 4,
          redirectUrl: window?.location?.href,
        });
        setShowVerify(true);
        setErrorMsg({
          show: false,
          type: undefined,
          msg: '',
        });
        setLoginAccessToken(data.accessToken);
        setLoginEmailSend(result);
        setLoginAccessStatus(code);
      } else {
        handleErrors(code, msg, navigate);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    // 调用重发邮件(的后端接口或逻辑
    const result = await loginVerifyEmail?.({ accessToken: loginAccessToken, rcVersion: 4 });
    setLoginEmailSend(result);
    // console.log('Resending email...');
    return result;
  };

  useEffect(() => {
    const hasAutoLogin = sessionStorage.getItem('hasAutoLogin') === 'true';
    if ((!hasAutoLogin && orderNumber) || (loginLayout?.urlShopName && orderNumber)) {
      handleFinish({ email, orderNumber });
      sessionStorage.setItem('hasAutoLogin', 'true');
    }
  }, []);

  useEffect(() => {
    if (errorMsg.show) {
      setErrorMsg({
        show: true,
        type: 'error',
        msg: t(
          'Sorry, the order cannot be found. Please check if the order number is correct and whether it has been canceled. If you have any other issues, please contact the merchant.',
        ),
      });
    }
    const hasNoErrors = form.getFieldsError().every((field) => field.errors.length === 0);
    if (!hasNoErrors) {
      form.validateFields();
    }
  }, [i18next.language]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = customStyleConfig?.advanced?.customCss ?? '';
    document.head.appendChild(style);

    // Cleanup function to remove the style tag
    return () => {
      document.head.removeChild(style);
    };
  }, [customStyleConfig?.advanced?.customCss]);

  return (
    <div className={styles.container}>
      {errorMsg.show && <ErrorTips errorText={errorMsg.msg} showSorry={false} type={errorMsg.type} />}
      <div className={styles.loginContainer}>
        {
          !showVerify && (
            <>
              <div
                className={styles.heading}
                style={{
                  ...generateStyle(customStyleConfig?.display),
                }}
              >
                {t('To begin, please provide us with your order details')}
              </div>
              <Form autoComplete="off" className={styles.fullWidth} form={form} initialValues={{ email, orderNumber }} onFinish={handleFinish}>
                <Form.Item
                  name="email"
                  rules={[
                    {
                      required: true,
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.reject(<ErrorMessage error={t('{{text}} is required', { text: emailLabelText })} />);
                        }
                        if (!(regEmail.test(value) || regPhoneLogin.test(value))) {
                          return Promise.reject(<ErrorMessage error={t('{{text}} is required', { text: emailLabelText })} />);
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    className={styles.input}
                    style={{
                      ...generateStyle(customStyleConfig?.input),
                    }}
                    placeholder={emailLabelText}
                  />
                </Form.Item>
                <Form.Item
                  name="orderNumber"
                  rules={[
                    {
                      required: true,
                      validator: (_, value) => {
                        if (!value) {
                          return Promise.reject(<ErrorMessage error={t('{{text}} is required', { text: orderLabelText })} />);
                        }

                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    className={styles.input}
                    placeholder={orderLabelText}
                    style={{
                      ...generateStyle(customStyleConfig?.input),
                    }}
                  />
                </Form.Item>
                <Form.Item noStyle>
                  <FormSubmitButton
                    className={styles.submitButton}
                    form={form}
                    loading={loading}
                    style={{
                      background: returnConfig.primaryColor,
                      ...generateStyle(customStyleConfig?.display, customStyleConfig?.button),
                    }}
                  >
                    {t('Next')}
                  </FormSubmitButton>
                </Form.Item>
                {!!returnConfig.merchantSupportEmail && <ContactEmail customStyleConfig={customStyleConfig} email={returnConfig.merchantSupportEmail} text={returnConfig.merchantSupportEmail} />}
              </Form>
            </>
          )
        }
        {
          showVerify && loginAccessToken && (
            <EmailVerification
              email={form?.getFieldValue('email')}
              customStyleConfig={customStyleConfig}
              isOpen={showVerify}
              loginAccessStatus={loginAccessStatus}
              loginEmailSend={loginEmailSend}
              loginAccessToken={loginAccessToken}
              onClose={() => setShowVerify(false)}
              onResend={handleResend}
            />
          )
        }
      </div>
    </div>
  );
};

export default LoginForm;
